import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { AppLink, useGo } from '../components/Nav';
import { UsersIcon } from '../components/icons';
import { Button, ErrorText, Input, PageHeader } from '../components/ui';
import { errorMessage } from '../lib/api';
import { createOrg } from '../queries/orgs';

/**
 * Creating a team. One field, and an honest account of what happens next —
 * a trial starts, seats are billed, and the person filling this in owns it.
 */

export function OrgNewPage() {
  const go = useGo();
  const { refetch, select } = useWorkspace();
  const [name, setName] = useState('');
  const [invalid, setInvalid] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (value: string) => createOrg(value),
    onSuccess: (response) => {
      if (response.checkoutUrl) {
        // Stripe Checkout. The API sends people back to the billing page after.
        window.location.assign(response.checkoutUrl);
        return;
      }
      refetch();
      select(response.org.id);
      go('/org/$slug', { params: { slug: response.org.slug } });
    },
  });

  const submit = () => {
    setInvalid(null);
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setInvalid('Give the workspace a name — usually your company or team.');
      return;
    }
    create.mutate(trimmed);
  };

  return (
    <div className="mx-auto w-full max-w-md">
      <PageHeader
        title="Create a team"
        subtitle="A shared workspace for recordings everyone should be able to watch."
      />

      <form
        className="card p-5"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Input
          label="Team name"
          placeholder="Northwind Design"
          value={name}
          maxLength={80}
          autoFocus
          onChange={(event) => setName(event.target.value)}
        />

        {invalid ? (
          <div className="mt-3">
            <ErrorText>{invalid}</ErrorText>
          </div>
        ) : null}
        {create.isError ? (
          <div className="mt-3">
            <ErrorText>{errorMessage(create.error)}</ErrorText>
          </div>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="mt-5 w-full"
          busy={create.isPending}
        >
          {create.isPending ? 'Creating' : 'Create team'}
        </Button>
      </form>

      <div className="card mt-4 p-5">
        <div className="flex items-center gap-2 text-chalk">
          <UsersIcon size={16} className="text-chalk-faint" />
          <h2 className="text-h4">What you are signing up for</h2>
        </div>
        <ul className="mt-3 space-y-2 text-body text-chalk-dim">
          <li>
            <span className="text-chalk">14 days free.</span> No card up front unless billing is
            configured on this deployment — if it is, you will land on Stripe Checkout next.
          </li>
          <li>
            <span className="text-chalk">Billed per seat</span> after the trial. Every active member
            counts as one; removing someone drops the seat.
          </li>
          <li>
            <span className="text-chalk">You become the owner.</span> Owners handle billing and can
            promote admins. Admins manage members and can see every recording in the org, including
            members&rsquo; personal ones.
          </li>
          <li>
            Your personal library stays yours and stays free — creating a team does not move
            anything into it.
          </li>
        </ul>
      </div>

      <p className="mt-4 text-center text-caption text-chalk-faint">
        Changed your mind?{' '}
        <AppLink to="/" className="text-signal hover:underline">
          Back to your library
        </AppLink>
      </p>
    </div>
  );
}
